import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  CalendarDays,
  Download,
  Edit3,
  Fuel,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { Badge, Button, Card, EmptyState, Field, InlineMessage, Modal, PageHeader } from "../components/ui";
import {
  activityCost,
  compactCurrency,
  currency,
  durationBetween,
  getNumericAssumption,
  monthIso,
  shortDate,
  todayIso,
} from "../lib/calculations";
import { activityTypes, type AppData, type TeamMember, type WorkActivity } from "../types";

interface WorkLogPageProps {
  data: AppData;
  profile: TeamMember;
  openNewToken: number;
  saveActivity(activity: WorkActivity): Promise<void>;
  deleteActivity(id: string): Promise<void>;
}

function newActivity(profile: TeamMember): WorkActivity {
  return {
    id: crypto.randomUUID(),
    work_date: todayIso(),
    team_member_id: profile.user_id,
    client_id: null,
    activity_type: "Weed cutting",
    description: "",
    start_time: "08:00",
    end_time: "10:00",
    duration_minutes: 120,
    status: "Completed",
    amount_charged: 0,
    amount_received: 0,
    payment_status: "Unpaid",
    petrol_litres: 0,
    two_stroke_oil_ml: 0,
    trimmer_line_cost: 0,
    refuse_bags_cost: 0,
    maintenance_cost: 0,
    materials_cost: 0,
    transport_cost: 0,
    phone_data_cost: 0,
    other_cost: 0,
    tools_used: [],
    notes: "",
  };
}

export function WorkLogPage({ data, profile, openNewToken, saveActivity, deleteActivity }: WorkLogPageProps) {
  const [month, setMonth] = useState(monthIso());
  const [status, setStatus] = useState("All");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<WorkActivity | null>(null);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (openNewToken > 0) setEditing(newActivity(profile));
  }, [openNewToken, profile]);

  const rows = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return data.activities.filter((item) => {
      const client = data.clients.find((row) => row.id === item.client_id);
      return (
        item.work_date.startsWith(month) &&
        (status === "All" || item.status === status) &&
        (!normalized ||
          item.activity_type.toLowerCase().includes(normalized) ||
          item.description.toLowerCase().includes(normalized) ||
          client?.client_name.toLowerCase().includes(normalized))
      );
    });
  }, [data.activities, data.clients, month, query, status]);

  const totals = rows.reduce(
    (result, item) => ({
      minutes: result.minutes + (item.status === "Completed" ? item.duration_minutes : 0),
      received: result.received + item.amount_received,
      petrol: result.petrol + item.petrol_litres,
      cost: result.cost + (item.status === "Completed" ? activityCost(item, data.assumptions) : 0),
    }),
    { minutes: 0, received: 0, petrol: 0, cost: 0 },
  );

  function exportCsv() {
    const header = [
      "Work date", "Team member", "Client", "Activity", "Description", "Start", "End",
      "Duration minutes", "Status", "Amount charged", "Amount received", "Payment status",
      "Petrol litres", "2-stroke oil ml", "Trimmer line cost", "Refuse bags cost",
      "Maintenance cost", "Materials cost", "Transport cost", "Phone/data cost", "Other cost",
      "Tools used", "Notes",
    ];
    const escape = (value: unknown) => {
      const raw = String(value ?? "");
      const spreadsheetSafe = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
      return `"${spreadsheetSafe.replaceAll('"', '""')}"`;
    };
    const csvRows = rows.map((item) => {
      const client = data.clients.find((row) => row.id === item.client_id);
      const member = data.team.find((row) => row.user_id === item.team_member_id);
      return [
        item.work_date, member?.display_name || "", client?.client_name || "", item.activity_type,
        item.description, item.start_time || "", item.end_time || "", item.duration_minutes,
        item.status, item.amount_charged, item.amount_received, item.payment_status,
        item.petrol_litres, item.two_stroke_oil_ml, item.trimmer_line_cost, item.refuse_bags_cost,
        item.maintenance_cost, item.materials_cost, item.transport_cost, item.phone_data_cost,
        item.other_cost, item.tools_used.join("; "), item.notes,
      ].map(escape).join(",");
    });
    const blob = new Blob([[header.map(escape).join(","), ...csvRows].join("\n")], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `house-care-work-log-${month}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  async function remove(item: WorkActivity) {
    if (!window.confirm(`Delete the ${item.activity_type.toLowerCase()} entry from ${item.work_date}?`)) return;
    setDeleteError("");
    try {
      await deleteActivity(item.id);
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : "Could not delete the entry.");
    }
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow="Daily records"
        title="Work log"
        description="Record each visit, time worked, payment, fuel and other job costs."
        actions={<><Button variant="secondary" onClick={exportCsv} disabled={rows.length === 0}><Download size={17} /> Export CSV</Button><Button onClick={() => setEditing(newActivity(profile))}><Plus size={18} /> Add work</Button></>}
      />

      <Card className="filters-card">
        <label className="search-field"><Search size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search client or activity" aria-label="Search work log" /></label>
        <label><span className="sr-only">Month</span><input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label>
        <label><span className="sr-only">Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option>All</option><option>Planned</option><option>Completed</option><option>Cancelled</option></select></label>
      </Card>

      <section className="log-summary" aria-label="Filtered work totals">
        <span><strong>{rows.length}</strong> entries</span>
        <span><strong>{(totals.minutes / 60).toFixed(1)}</strong> hours</span>
        <span><strong>{compactCurrency.format(totals.received)}</strong> received</span>
        <span><strong>{totals.petrol.toFixed(1)} L</strong> petrol</span>
        <span><strong>{compactCurrency.format(totals.cost)}</strong> costs</span>
      </section>

      {deleteError && <InlineMessage>{deleteError}</InlineMessage>}

      {rows.length === 0 ? (
        <Card><EmptyState title="No entries match this view" description="Change the month or filters, or add the first work entry." action={<Button onClick={() => setEditing(newActivity(profile))}>Add work</Button>} /></Card>
      ) : (
        <div className="log-table-wrap card">
          <table className="data-table work-table">
            <thead><tr><th>Date</th><th>Client and work</th><th>Team member</th><th>Time</th><th>Payment</th><th>Fuel</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {rows.map((item) => {
                const client = data.clients.find((row) => row.id === item.client_id);
                const member = data.team.find((row) => row.user_id === item.team_member_id);
                const canEdit = profile.role === "owner" || item.team_member_id === profile.user_id;
                return (
                  <tr key={item.id}>
                    <td><strong>{shortDate.format(new Date(`${item.work_date}T00:00:00`))}</strong></td>
                    <td><strong>{client?.client_name || "General work"}</strong><span>{item.activity_type}{item.description ? ` · ${item.description}` : ""}</span></td>
                    <td>{member?.display_name || "Team member"}</td>
                    <td>{item.start_time?.slice(0, 5) || "—"}–{item.end_time?.slice(0, 5) || "—"}<span>{(item.duration_minutes / 60).toFixed(1)} h</span></td>
                    <td><strong>{item.amount_received > 0 ? currency.format(item.amount_received) : "—"}</strong><span>{item.payment_status}</span></td>
                    <td>{item.petrol_litres > 0 ? <span className="fuel-value"><Fuel size={16} />{item.petrol_litres.toFixed(2)} L</span> : "—"}</td>
                    <td><Badge tone={item.status === "Completed" ? "success" : item.status === "Cancelled" ? "danger" : "warning"}>{item.status}</Badge></td>
                    <td>{canEdit ? <div className="table-actions"><button className="icon-button" onClick={() => setEditing({ ...item })} aria-label="Edit work entry"><Edit3 size={17} /></button><button className="icon-button danger-icon" onClick={() => void remove(item)} aria-label="Delete work entry"><Trash2 size={17} /></button></div> : <span className="muted">View only</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <WorkActivityModal
        open={Boolean(editing)}
        activity={editing}
        data={data}
        onClose={() => setEditing(null)}
        onSave={async (activity) => { await saveActivity(activity); setEditing(null); }}
      />
    </div>
  );
}

function WorkActivityModal({ open, activity, data, onClose, onSave }: { open: boolean; activity: WorkActivity | null; data: AppData; onClose(): void; onSave(activity: WorkActivity): Promise<void> }) {
  const [form, setForm] = useState<WorkActivity | null>(activity);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => setForm(activity ? { ...activity, tools_used: [...activity.tools_used] } : null), [activity]);
  if (!form) return null;

  const isWeedCutter = form.tools_used.includes("Petrol brush cutter / weed cutter");
  const directCosts = activityCost(form, data.assumptions);
  const set = <K extends keyof WorkActivity>(key: K, value: WorkActivity[K]) => setForm((current) => current ? { ...current, [key]: value } : current);
  const number = (value: string) => Math.max(0, Number(value) || 0);

  function chooseClient(clientId: string) {
    const client = data.clients.find((item) => item.id === clientId);
    setForm((current) => current ? { ...current, client_id: clientId || null, amount_charged: client?.amount_per_payment ?? current.amount_charged } : current);
  }

  function toggleWeedCutter(checked: boolean) {
    const litres = getNumericAssumption(data.assumptions, "weed_cutter_petrol_per_visit", 0.5);
    const mix = getNumericAssumption(data.assumptions, "two_stroke_oil_mix_ml_per_litre", 20);
    setForm((current) => {
      if (!current) return current;
      const tools = checked
        ? Array.from(new Set([...current.tools_used, "Petrol brush cutter / weed cutter"]))
        : current.tools_used.filter((item) => item !== "Petrol brush cutter / weed cutter");
      return { ...current, tools_used: tools, petrol_litres: checked ? current.petrol_litres || litres : current.petrol_litres, two_stroke_oil_ml: checked ? current.two_stroke_oil_ml || litres * mix : current.two_stroke_oil_ml };
    });
  }

  function toggleTool(toolName: string, checked: boolean) {
    setForm((current) => current ? { ...current, tools_used: checked ? Array.from(new Set([...current.tools_used, toolName])) : current.tools_used.filter((item) => item !== toolName) } : current);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const submitted = form;
    if (!submitted) return;
    setBusy(true);
    setError("");
    try {
      await onSave({ ...submitted, duration_minutes: submitted.start_time && submitted.end_time ? durationBetween(submitted.start_time, submitted.end_time) : submitted.duration_minutes });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save this work entry.");
      setBusy(false);
    }
  }

  const selectableTools = data.tools.filter((tool) => tool.included || tool.already_owned);

  return (
    <Modal open={open} title={activity?.created_at ? "Edit work entry" : "Add daily work"} description="Required fields are marked with an asterisk." onClose={onClose} wide>
      <form onSubmit={submit} className="activity-form">
        <section className="form-section"><h3>Visit details</h3><div className="form-grid form-grid-3">
          <Field label="Work date" required><input type="date" value={form.work_date} onChange={(event) => set("work_date", event.target.value)} required /></Field>
          <Field label="Client"><select value={form.client_id || ""} onChange={(event) => chooseClient(event.target.value)}><option value="">General / no client</option>{data.clients.map((client) => <option key={client.id} value={client.id}>{client.client_name}</option>)}</select></Field>
          <Field label="Status" required><select value={form.status} onChange={(event) => set("status", event.target.value as WorkActivity["status"])}><option>Planned</option><option>Completed</option><option>Cancelled</option></select></Field>
          <Field label="Activity" required><select value={form.activity_type} onChange={(event) => set("activity_type", event.target.value as WorkActivity["activity_type"])}>{activityTypes.map((type) => <option key={type}>{type}</option>)}</select></Field>
          <Field label="Start time"><input type="time" value={form.start_time || ""} onChange={(event) => set("start_time", event.target.value || null)} /></Field>
          <Field label="End time"><input type="time" value={form.end_time || ""} onChange={(event) => set("end_time", event.target.value || null)} /></Field>
          <Field label="Work description" className="field-span-3"><textarea rows={2} value={form.description} onChange={(event) => set("description", event.target.value)} placeholder="What was completed?" /></Field>
        </div></section>

        <section className="form-section"><div className="section-heading"><div><h3>Tools and fuel</h3><p>Use the workbook assumptions as a starting point, then enter actual usage.</p></div></div><label className="switch-row"><input type="checkbox" checked={isWeedCutter} onChange={(event) => toggleWeedCutter(event.target.checked)} /><span className="switch-control" /><span><strong>Weed cutter used</strong><small>Prefills petrol and 2-stroke oil from the current assumptions.</small></span></label><div className="form-grid form-grid-3">
          <Field label="Petrol used" hint="litres"><input type="number" min="0" step="0.01" value={form.petrol_litres} onChange={(event) => { const litres = number(event.target.value); setForm((current) => current ? { ...current, petrol_litres: litres, two_stroke_oil_ml: isWeedCutter ? litres * getNumericAssumption(data.assumptions, "two_stroke_oil_mix_ml_per_litre", 20) : current.two_stroke_oil_ml } : current); }} /></Field>
          <Field label="2-stroke oil" hint="millilitres"><input type="number" min="0" step="1" value={form.two_stroke_oil_ml} onChange={(event) => set("two_stroke_oil_ml", number(event.target.value))} /></Field>
          <div className="calculated-box"><span>Calculated direct costs</span><strong>{currency.format(directCosts)}</strong></div>
        </div><details className="tools-details"><summary>Select other tools used ({form.tools_used.length})</summary><div className="check-grid">{selectableTools.map((tool) => <label key={tool.id}><input type="checkbox" checked={form.tools_used.includes(tool.tool_name)} onChange={(event) => toggleTool(tool.tool_name, event.target.checked)} />{tool.tool_name}</label>)}</div></details></section>

        <section className="form-section"><h3>Payment</h3><div className="form-grid form-grid-3">
          <Field label="Amount charged"><input type="number" min="0" step="0.01" value={form.amount_charged} onChange={(event) => set("amount_charged", number(event.target.value))} /></Field>
          <Field label="Amount received"><input type="number" min="0" step="0.01" value={form.amount_received} onChange={(event) => set("amount_received", number(event.target.value))} /></Field>
          <Field label="Payment status"><select value={form.payment_status} onChange={(event) => set("payment_status", event.target.value as WorkActivity["payment_status"])}><option>Not due</option><option>Unpaid</option><option>Part paid</option><option>Paid</option></select></Field>
        </div></section>

        <section className="form-section"><h3>Other job costs</h3><div className="form-grid form-grid-3 compact-fields">
          <Field label="Trimmer line"><input type="number" min="0" step="0.01" value={form.trimmer_line_cost} onChange={(event) => set("trimmer_line_cost", number(event.target.value))} /></Field>
          <Field label="Refuse bags"><input type="number" min="0" step="0.01" value={form.refuse_bags_cost} onChange={(event) => set("refuse_bags_cost", number(event.target.value))} /></Field>
          <Field label="Maintenance"><input type="number" min="0" step="0.01" value={form.maintenance_cost} onChange={(event) => set("maintenance_cost", number(event.target.value))} /></Field>
          <Field label="Materials"><input type="number" min="0" step="0.01" value={form.materials_cost} onChange={(event) => set("materials_cost", number(event.target.value))} /></Field>
          <Field label="Transport"><input type="number" min="0" step="0.01" value={form.transport_cost} onChange={(event) => set("transport_cost", number(event.target.value))} /></Field>
          <Field label="Phone / data"><input type="number" min="0" step="0.01" value={form.phone_data_cost} onChange={(event) => set("phone_data_cost", number(event.target.value))} /></Field>
          <Field label="Other cost"><input type="number" min="0" step="0.01" value={form.other_cost} onChange={(event) => set("other_cost", number(event.target.value))} /></Field>
          <Field label="Notes" className="field-span-2"><textarea rows={2} value={form.notes} onChange={(event) => set("notes", event.target.value)} placeholder="Problems, follow-up work or payment notes" /></Field>
        </div></section>
        {error && <InlineMessage>{error}</InlineMessage>}
        <footer className="form-actions"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" disabled={busy}>{busy ? "Saving…" : "Save work entry"}</Button></footer>
      </form>
    </Modal>
  );
}
